import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser, ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

// NOT: /api/customer-memberships OData değil; özel REST endpoint (ApiResponse<{ items, total, ... }>).
// Bu yüzden useC360List (OData $filter/$orderby) yerine veri tek seferde çekilir ve
// filtre/sıralama/sayfalama istemci tarafında yapılır. c360 görsel kabuğu birebir korunur.
const API_BASE = '/api/customer-memberships';
const PAGE_SIZE_DEFAULT = 20;

const FILTER_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'hva', label: 'HVA Var' },
  { key: 'kazandirio', label: 'Kazandırio Var' },
  { key: 'photo', label: 'Fotoğraf Var' },
];

const HAS_OPTIONS = [
  { value: 'true', label: 'Var' },
  { value: 'false', label: 'Yok' },
];

// İstemci-taraflı filtre meta (C360Table'ın filtre popover'ları için). odataField burada
// yalnızca etiket/kimlik amaçlı; backend OData olmadığından filtreleme istemcide uygulanır.
const COLUMN_FILTER_META = {
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  hvaTierName: { type: 'text', odataField: 'HvaTierName' },
  hasKazandirio: { type: 'select', odataField: 'HasKazandirio', valueFormat: 'raw', options: HAS_OPTIONS },
  hasPhoto: { type: 'select', odataField: 'HasPhoto', valueFormat: 'raw', options: HAS_OPTIONS },
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    total: pick(data, 'total', 'Total') ?? 0,
    withHvaCount: pick(data, 'withHvaCount', 'WithHvaCount') ?? 0,
    withPhotosCount: pick(data, 'withPhotosCount', 'WithPhotosCount') ?? 0,
    withKazandirioCount: pick(data, 'withKazandirioCount', 'WithKazandirioCount') ?? 0,
  };
}

function parseKazandirio(json) {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function parsePhotos(csv) {
  if (!csv) return [];
  return String(csv).split(',').map((u) => u.trim()).filter(Boolean);
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function HvaProgress({ current, target, tier, compact }) {
  const cur = Number(current);
  const tgt = Number(target);
  const hasData = Number.isFinite(cur) && cur >= 0;
  if (!hasData && !tier) return <span style={{ color: '#94a3b8' }}>-</span>;
  const pct = Number.isFinite(tgt) && tgt > 0 ? Math.min(100, Math.round((cur / tgt) * 100)) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: compact ? 120 : 200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: compact ? 11 : 12, fontWeight: 600 }}>
        <span style={{ color: '#5b21b6' }}>{tier || 'HVA'}</span>
        <span style={{ color: '#6b7280' }}>
          {Number.isFinite(cur) ? cur : '-'}{Number.isFinite(tgt) && tgt > 0 ? ` / ${tgt}` : ''}
        </span>
      </div>
      <div style={{ height: compact ? 6 : 8, borderRadius: 999, background: 'rgba(139, 92, 246, 0.15)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #8b5cf6, #6366f1)', borderRadius: 999 }} />
      </div>
    </div>
  );
}

// Satırı normalize et (camel/Pascal karışık gelebilir).
function normalizeRow(row) {
  const kaz = parseKazandirio(pick(row, 'kazandirioItemsJson', 'KazandirioItemsJson'));
  const photos = parsePhotos(pick(row, 'frontPhotoUrlsCsv', 'FrontPhotoUrlsCsv'));
  return {
    id: pick(row, 'id', 'Id'),
    customerId: pick(row, 'customerId', 'CustomerId'),
    customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
    hvaCurrentPoints: pick(row, 'hvaCurrentPoints', 'HvaCurrentPoints'),
    hvaTargetPoints: pick(row, 'hvaTargetPoints', 'HvaTargetPoints'),
    hvaTierName: pick(row, 'hvaTierName', 'HvaTierName') || '',
    kazandirioItems: kaz,
    kazandirioCount: kaz.length,
    photos,
    photoCount: photos.length,
    updatedDate: pick(row, 'updatedDate', 'UpdatedDate'),
  };
}

export function CustomerMembershipsPage() {
  usePageMeta('Müşteri Üyelik & Puan', 'HVA puan ilerlemesi, KazandıRio ürünleri ve tabela fotoğrafları.');

  const [allRows, setAllRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, withHvaCount: 0, withPhotosCount: 0, withKazandirioCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [filterTab, setFilterTab] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows((Array.isArray(data.items) ? data.items : []).map(normalizeRow));
      setCounts({
        total: data.total,
        withHvaCount: data.withHvaCount,
        withPhotosCount: data.withPhotosCount,
        withKazandirioCount: data.withKazandirioCount,
      });
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // İstemci-taraflı filtre + sıralama.
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const tabMatch = (row) => {
      if (filterTab === 'hva') return row.hvaTierName || row.hvaCurrentPoints != null;
      if (filterTab === 'kazandirio') return row.kazandirioCount > 0;
      if (filterTab === 'photo') return row.photoCount > 0;
      return true;
    };
    const colMatch = (row) => {
      for (const [field, filter] of Object.entries(columnFilters)) {
        if (!filter || filter.value === '' || filter.value == null) continue;
        if (field === 'customerTitle') {
          if (!String(row.customerTitle).toLocaleLowerCase('tr-TR').includes(String(filter.value).toLocaleLowerCase('tr-TR'))) return false;
        } else if (field === 'hvaTierName') {
          if (!String(row.hvaTierName).toLocaleLowerCase('tr-TR').includes(String(filter.value).toLocaleLowerCase('tr-TR'))) return false;
        } else if (field === 'hasKazandirio') {
          const want = filter.value === 'true';
          if ((row.kazandirioCount > 0) !== want) return false;
        } else if (field === 'hasPhoto') {
          const want = filter.value === 'true';
          if ((row.photoCount > 0) !== want) return false;
        }
      }
      return true;
    };
    const searchMatch = (row) => {
      if (!needle) return true;
      return [row.customerTitle, row.hvaTierName].some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = allRows.filter((r) => tabMatch(r) && colMatch(r) && searchMatch(r));

    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CustomerTitle': return row.customerTitle || '';
        case 'HvaTierName': return row.hvaTierName || '';
        case 'HvaCurrentPoints': return Number(row.hvaCurrentPoints) || 0;
        case 'UpdatedDate':
        default: return row.updatedDate ? new Date(row.updatedDate).getTime() : 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterTab, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (id, next) => { setColumnFilters((p) => ({ ...p, [id]: next })); setCurrentPage(1); };
  const clearColumnFilter = (id) => { setColumnFilters((p) => { const c = { ...p }; delete c[id]; return c; }); setCurrentPage(1); };
  const applySort = (field, dir) => { setSortField(field || 'UpdatedDate'); setSortDir(dir || 'desc'); setCurrentPage(1); };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'hva', header: 'HVA İlerleme',
      cell: ({ row }) => (
        <HvaProgress
          compact
          current={row.original.hvaCurrentPoints}
          target={row.original.hvaTargetPoints}
          tier={row.original.hvaTierName}
        />
      ),
    }),
    columnHelper.accessor('hvaTierName', {
      header: 'Kademe', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('kazandirioCount', {
      id: 'hasKazandirio', header: 'Kazandırio', meta: { filterType: 'select' },
      cell: (info) => { const n = info.getValue(); return n > 0 ? <StatusBadge tone="ok">{`${n} ürün`}</StatusBadge> : <StatusBadge tone="bad">Yok</StatusBadge>; },
    }),
    columnHelper.accessor('photoCount', {
      id: 'hasPhoto', header: 'Fotoğraf', meta: { filterType: 'select' },
      cell: (info) => { const n = info.getValue(); return n > 0 ? <StatusBadge tone="ok">{n}</StatusBadge> : <StatusBadge tone="bad">Yok</StatusBadge>; },
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDetailRow(row.original); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { field: 'customerTitle', headerName: 'Müşteri' },
        { headerName: 'Kademe', valueGetter: ({ row }) => row.hvaTierName || '' },
        { headerName: 'HVA Puan', valueGetter: ({ row }) => (row.hvaCurrentPoints ?? '') },
        { headerName: 'HVA Hedef', valueGetter: ({ row }) => (row.hvaTargetPoints ?? '') },
        { headerName: 'Kazandırio Ürün', valueGetter: ({ row }) => row.kazandirioCount },
        { headerName: 'Fotoğraf', valueGetter: ({ row }) => row.photoCount },
        { headerName: 'Güncelleme', valueGetter: ({ row }) => formatDate(row.updatedDate) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `musteri-uyelik_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <div style={{ display: 'inline-flex', gap: 6 }}>
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key || 'all'}
            type="button"
            onClick={() => { setFilterTab(tab.key); setCurrentPage(1); }}
            style={{ ...(filterTab === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Müşteri / kademe ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const detailKazandirio = detailRow ? detailRow.kazandirioItems : [];
  const detailPhotos = detailRow ? detailRow.photos : [];

  return (
    <div style={s.container}>
      <ListPageShell
        title="Müşteri Üyelik & Puan"
        subtitle="Müşteri başına HVA puan ilerlemesi, KazandıRio sadakat ürünleri ve tabela fotoğrafları. Mobile delta ile çeker; admin görüntüleme."
        actions={(
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusBadge tone="neutral">{`Toplam: ${counts.total}`}</StatusBadge>
            <StatusBadge tone="ok">{`HVA: ${counts.withHvaCount}`}</StatusBadge>
            <StatusBadge tone="ok">{`Kazandırio: ${counts.withKazandirioCount}`}</StatusBadge>
            <StatusBadge tone="wait">{`Fotoğraflı: ${counts.withPhotosCount}`}</StatusBadge>
          </div>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => { setSelected(row); setDetailRow(row); }}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{detailRow.customerTitle || 'Müşteri Üyelik'}</h3>
              <button style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>HVA İlerleme</label>
                <HvaProgress
                  current={detailRow.hvaCurrentPoints}
                  target={detailRow.hvaTargetPoints}
                  tier={detailRow.hvaTierName}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>KazandıRio Ürünleri ({detailKazandirio.length})</label>
                {detailKazandirio.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>Kayıt yok</div>
                ) : (
                  <div style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10, overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: 'var(--bg-card, #f9fafb)' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ürün</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Adet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailKazandirio.map((item, idx) => (
                          <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>{pick(item, 'productName', 'ProductName') || '-'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{pick(item, 'quantity', 'Quantity') ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Tabela Fotoğrafları ({detailPhotos.length})</label>
                {detailPhotos.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>Fotoğraf yok</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {detailPhotos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noreferrer"
                         style={{ display: 'block', width: 120, height: 120, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-color, #e5e7eb)' }}>
                        <img
                          src={url}
                          alt={`Tabela ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Son Güncelleme</label>
                <div style={s.inputView}>{formatDate(detailRow.updatedDate)}</div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CustomerMembershipsPage;
